import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, RotateCcw, XCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router';

import { Button } from '../../components/ui/button';
import { useConfirmPayment } from '../../query/payment';

type Status = 'confirming' | 'success' | 'error';

/** 일시적 오류(네트워크 끊김·서버 순간 장애)는 자동 재시도한다. */
const MAX_ATTEMPTS = 3;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * 재시도해서 풀릴 수 있는 오류인지 판단한다.
 * - status 없음: fetch 자체 실패(네트워크 끊김/서버 다운) → 재시도 가치 있음
 * - 5xx: 서버 일시 장애 → 재시도 가치 있음
 * - 4xx: 금액 불일치·타인 주문 등 → 다시 해도 같은 결과
 */
const isRetryable = (error: unknown): boolean => {
  const status = (error as { status?: number })?.status;
  return status === undefined || status >= 500;
};

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: confirmPaymentAsync } = useConfirmPayment();

  const [status, setStatus] = useState<Status>('confirming');
  const [message, setMessage] = useState('');
  const [attempt, setAttempt] = useState(1);
  const [canRetry, setCanRetry] = useState(false);
  const requestedRef = useRef(false);

  const paymentKey = searchParams.get('paymentKey');
  const orderId = searchParams.get('orderId');
  const amount = searchParams.get('amount');

  const runConfirm = useCallback(async () => {
    if (!paymentKey || !orderId || !amount) {
      setStatus('error');
      setCanRetry(false);
      setMessage('결제 정보가 올바르지 않습니다.');
      return;
    }

    setStatus('confirming');
    for (let n = 1; n <= MAX_ATTEMPTS; n++) {
      setAttempt(n);
      try {
        // ⚠️ mutate(vars, { onSuccess })의 콜백은 옵저버가 언마운트되면 버려진다.
        // (StrictMode의 언마운트→재마운트 사이에 응답이 오면 화면이 로딩에 갇힌다)
        // mutateAsync는 옵저버 생명주기와 무관하게 결과를 받으므로 안전하다.
        await confirmPaymentAsync({ paymentKey, orderId, amount: Number(amount) });
        setStatus('success');
        queryClient.invalidateQueries({ queryKey: ['student', 'course'] });
        return;
      } catch (error: unknown) {
        const retryable = isRetryable(error);
        if (retryable && n < MAX_ATTEMPTS) {
          await sleep(n * 1000); // 1초 → 2초 백오프
          continue;
        }
        setStatus('error');
        setCanRetry(retryable);
        setMessage((error as { message?: string })?.message ?? '결제 승인에 실패했습니다.');
        return;
      }
    }
  }, [paymentKey, orderId, amount, confirmPaymentAsync, queryClient]);

  useEffect(() => {
    // Toss 리다이렉트로 진입. 최초 승인 시도는 1회만 자동 실행한다.
    if (requestedRef.current) return;
    requestedRef.current = true;
    void runConfirm();
  }, [runConfirm]);

  return (
    <div className="flex min-h-[70vh] items-center justify-center p-6">
      <div className="w-full max-w-md rounded-xl border bg-white p-8 text-center shadow-sm">
        {status === 'confirming' && (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <h1 className="text-lg font-semibold">결제를 확인하고 있습니다…</h1>
            <p className="text-sm text-muted-foreground">
              {attempt > 1 ? `연결이 불안정해 다시 확인하고 있어요. (${attempt}/${MAX_ATTEMPTS})` : '잠시만 기다려주세요.'}
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col items-center gap-4">
            <CheckCircle2 className="h-14 w-14 text-emerald-500" />
            <h1 className="text-xl font-bold">결제가 완료되었습니다</h1>
            <p className="text-sm text-muted-foreground">강좌 참여가 완료되었어요.</p>
            <Button className="mt-2 w-full" onClick={() => navigate('/course')}>
              내 강좌로 이동
            </Button>
          </div>
        )}

        {/* 재시도로 풀릴 수 있는 오류: 결제 자체는 됐을 수 있으므로 "실패"라고 단정하지 않는다. */}
        {status === 'error' && canRetry && (
          <div className="flex flex-col items-center gap-4">
            <AlertTriangle className="h-14 w-14 text-amber-500" />
            <h1 className="text-xl font-bold">결제 확인이 지연되고 있습니다</h1>
            <p className="text-sm text-muted-foreground">
              결제는 정상적으로 완료되었을 수 있습니다. 다시 시도해도 <strong>중복 결제되지 않으니</strong> 안심하세요.
            </p>
            {message && <p className="text-xs text-muted-foreground">{message}</p>}
            <Button className="mt-2 w-full gap-1.5" onClick={() => void runConfirm()}>
              <RotateCcw className="h-4 w-4" />
              다시 시도
            </Button>
            <Button variant="outline" className="w-full" onClick={() => navigate('/payment')}>
              내 결제 내역 확인
            </Button>
          </div>
        )}

        {/* 재시도해도 소용없는 오류(잘못된 주문·금액 불일치 등) */}
        {status === 'error' && !canRetry && (
          <div className="flex flex-col items-center gap-4">
            <XCircle className="h-14 w-14 text-red-500" />
            <h1 className="text-xl font-bold">결제 승인에 실패했습니다</h1>
            <p className="text-sm text-muted-foreground">{message}</p>
            <p className="text-xs text-muted-foreground">
              결제가 이루어졌다면 결제 내역에서 확인하실 수 있습니다.
            </p>
            <Button variant="outline" className="mt-2 w-full" onClick={() => navigate('/payment')}>
              내 결제 내역 확인
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => navigate('/course')}>
              강좌 목록으로 돌아가기
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
